import React, { Dispatch, SetStateAction, useEffect } from "react";

import IconXMark from "@images/pressClassification/icon-reason-x-mark.svg";

import {
  REVIEW_REQUIRED_REASON_ID,
  UNSUITABLE_REASON_TYPE_INFO,
  UnsuitableReasonList,
  UnsuitableReasonType,
} from "./constants/UnsuitableReason.constants";
import { useHandleUnsuitableReasonItem } from "./hooks/useHandleUnsuitableReasonItem";
import * as S from "./UnsuitableReasonCheckItem.style";
import { useCheckReviewRequired } from "../../../../hooks/useCheckReviewRequired";
import { UnsuitableReasonUsageType } from "../../../../types/unsuitableReason.types";

export interface InfoForSelectedRejectReason
  extends React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  reviewIdList?: number[] | null;
  reasonId: string;
  reason: string | null;
  explanation?: string;
  reasonType: UnsuitableReasonType;
  placeholder?: string;
  isTarget: boolean;
  duplId?: number | null;
  articleRejectReason: string | null;
  unsuitablePressReasonList: UnsuitableReasonList[];
  setUnsuitablePressReasonList: Dispatch<
    SetStateAction<UnsuitableReasonList[]>
  >;
  draftArticleId: number;
  unsuitableReasonUsageType: UnsuitableReasonUsageType;
  closeModalUnsuitablePress?: () => void;
  descriptionComponent?: React.ReactNode;
}

const UnsuitableReasonCheckItem = ({
  reviewIdList = null,
  descriptionComponent,
  reasonId,
  reason,
  explanation,
  reasonType,
  placeholder,
  isTarget,
  duplId,
  articleRejectReason,
  unsuitablePressReasonList,
  setUnsuitablePressReasonList,
  draftArticleId,
  unsuitableReasonUsageType,
  closeModalUnsuitablePress,
}: InfoForSelectedRejectReason) => {
  const { isUnsuitableWithReviewRequired } = useCheckReviewRequired(
    unsuitableReasonUsageType,
  );
  const infoForSelectedRejectReason = {
    reasonId,
    reason,
    explanation,
    reasonType,
    placeholder,
    isTarget,
    duplId,
    articleRejectReason,
    unsuitablePressReasonList,
    setUnsuitablePressReasonList,
    draftArticleId,
    unsuitableReasonUsageType,
    closeModalUnsuitablePress,
  };
  const {
    handleCheckTargetReason,
    handleDeleteReasonInput,
    handleClickCheckBox,
    handleKeyDownEnterSavedReason,
    handleFocusOutAddIdReason,
    inputRef,
  } = useHandleUnsuitableReasonItem(infoForSelectedRejectReason);

  const handleReturnDefaultValue = () => {
    if (reasonId === REVIEW_REQUIRED_REASON_ID)
      return reviewIdList?.join(", ") || "";
    if (reasonType === "addId") return duplId;
    if (reasonType === "writing") return reason;
  };

  const isNotDefaultType =
    reasonType === UNSUITABLE_REASON_TYPE_INFO.writing ||
    reasonType === UNSUITABLE_REASON_TYPE_INFO.addId;

  const isNotFocusedInput =
    inputRef.current && reasonType !== "default" && !isTarget;

  if (isNotFocusedInput) inputRef.current.value = "";

  const handleCheckNumberInput = () => {
    if (!inputRef.current) return;

    inputRef.current.value = inputRef.current.value.replace(/[^0-9]/g, "");
  };

  useEffect(() => {
    if (reasonId === REVIEW_REQUIRED_REASON_ID && isTarget === true) {
      if (!inputRef.current) return;
      inputRef.current.value = String(reviewIdList?.join(", ") || "") || "";
    }
  }, [reasonId, isTarget, reviewIdList, inputRef]);

  // NOTE: 리뷰 필요 섹션이 아닌 경우 리뷰 필요 체크 항목 노출 안함.

  if (reasonId === REVIEW_REQUIRED_REASON_ID && !isUnsuitableWithReviewRequired)
    return null;
  return (
    <S.CheckItemLabelWrapper
      onChange={() => handleCheckTargetReason(reasonType)}
    >
      <S.Checkbox
        type="checkbox"
        checked={isTarget}
        onClick={() => handleClickCheckBox(reasonType)}
      />
      <S.CheckItemLabel isTarget={isTarget}>
        {isNotDefaultType ? (
          <>
            {explanation} <br />
            {descriptionComponent && descriptionComponent}
            <S.ReasonInputWrapper $isFilled={!!inputRef.current?.value}>
              <S.ReasonInput
                type="text"
                placeholder={placeholder}
                onInput={
                  reasonType === "addId" ? handleCheckNumberInput : undefined
                }
                onKeyDown={handleKeyDownEnterSavedReason}
                onBlur={() => handleFocusOutAddIdReason(reasonType)}
                defaultValue={handleReturnDefaultValue() || ""}
                maxLength={500}
                ref={inputRef}
              />
              {isTarget && (
                <S.ResetButton
                  data-button="delete reason button"
                  onClick={() => handleDeleteReasonInput(reasonType)}
                  type="button"
                >
                  <img
                    data-button="delete reason button"
                    src={IconXMark}
                    alt="delete reason"
                  />
                </S.ResetButton>
              )}
            </S.ReasonInputWrapper>
          </>
        ) : (
          reason
        )}
      </S.CheckItemLabel>
    </S.CheckItemLabelWrapper>
  );
};

export default UnsuitableReasonCheckItem;
